<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import CopilotAssumedActions from 'dashboard/components-next/message/CopilotAssumedActions.vue';
import { MESSAGE_TYPES, MESSAGE_VARIANTS } from '../../constants';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useMessageTranslation } from 'dashboard/composables/useMessageTranslation';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  variant,
  status,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const { currentTranslationMode } = useMessageTranslation();

const renderOriginal = ref(currentTranslationMode.value === 'original');
const showingOriginal = ref(currentTranslationMode.value === 'original');

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

// State of a WhatsApp request for the contact's phone number (see RequestContactInfoButton).
const contactInfoRequestState = computed(() => {
  if (status.value === MESSAGE_STATUS.FAILED) return null;

  return contentAttributes.value?.whatsappContactInfo?.state;
});

// What a bot reply draft assumes the agent does before sending it.
const assumedActions = computed(() => {
  const jarabot = contentAttributes.value?.jarabot;
  return (jarabot?.copilot && jarabot.assumedActions) || [];
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const isShowingTranslation = computed(() => {
  return hasTranslations.value && !renderOriginal.value;
});

const translatedBgClass = computed(() => {
  if (!isShowingTranslation.value) return '';

  switch (variant.value) {
    case MESSAGE_VARIANTS.AGENT:
      return '!bg-n-blue-3';
    case MESSAGE_VARIANTS.BOT:
    case MESSAGE_VARIANTS.TEMPLATE:
      return '!bg-n-iris-3';
    case MESSAGE_VARIANTS.PRIVATE:
      return '!bg-n-amber-3';
    case MESSAGE_VARIANTS.USER:
    default:
      return '!bg-n-slate-3';
  }
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
  showingOriginal.value = !showingOriginal.value;
};
</script>

<template>
  <BaseBubble
    class="relative px-4 py-3"
    :class="translatedBgClass"
    data-bubble-name="text"
  >
    <div class="gap-3 flex flex-col">
      <CopilotAssumedActions
        v-if="assumedActions.length"
        :actions="assumedActions"
      />
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <span
        v-if="contactInfoRequestState === 'pending'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.PENDING') }}
      </span>
      <span
        v-else-if="contactInfoRequestState === 'shared'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.SHARED') }}
      </span>
      <span
        v-else-if="contactInfoRequestState === 'identity_conflict'"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ $t('CONVERSATION.REQUEST_CONTACT_INFO.STATES.IDENTITY_CONFLICT') }}
      </span>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="showingOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
